import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import { CTASection } from "@/pages/Home";
import useSEO from "@/hooks/useSEO";
import { PORTFOLIO, PORTFOLIO_CATEGORIES } from "@/data/content";

const Portfolio = () => {
  useSEO(
    "Portfolio — SSDE LTD | Design Concepts & Creative Work",
    "Browse SSDE LTD's portfolio of design concepts across branding, logo design, print, packaging, website layouts, UI design and digital assets."
  );
  const [active, setActive] = useState("All");

  const items = useMemo(
    () => (active === "All" ? PORTFOLIO : PORTFOLIO.filter((p) => p.category === active)),
    [active]
  );

  return (
    <>
      <PageHero
        testId="portfolio-hero"
        eyebrow="Portfolio"
        titleLines={["Selected Work", "& Concepts."]}
        lede="A curated selection of design studies and concept projects. Where client work is not shown, pieces are clearly labelled as concepts created to demonstrate our approach."
      />

      <section className="bg-white py-16 sm:py-24" data-testid="portfolio-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex flex-wrap gap-2.5 mb-12" role="tablist" aria-label="Filter portfolio by category">
            {PORTFOLIO_CATEGORIES.map((cat) => (
              <button
                key={cat}
                type="button"
                role="tab"
                aria-selected={active === cat}
                data-testid={`portfolio-filter-${cat.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                onClick={() => setActive(cat)}
                className={`px-5 py-2.5 text-[13px] font-medium tracking-wide border transition-all duration-300 ${
                  active === cat
                    ? "bg-charcoal text-white border-charcoal"
                    : "bg-white text-charcoal-mute border-line hover:border-gold hover:text-gold-deep"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {items.length === 0 ? (
            <div className="border border-line bg-offwhite py-20 text-center" data-testid="portfolio-empty">
              <p className="font-serif text-2xl text-charcoal">Nothing here yet.</p>
              <p className="mt-2 text-sm text-charcoal-mute">Try another category.</p>
            </div>
          ) : (
            <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="portfolio-grid">
              <AnimatePresence mode="popLayout">
                {items.map((item, i) => (
                  <motion.article
                    layout
                    key={item.id}
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.97 }}
                    transition={{ duration: 0.5, delay: i * 0.04, ease: [0.16, 1, 0.3, 1] }}
                    data-testid={`portfolio-card-${item.id}`}
                    className="group bg-white border border-line overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_60px_-28px_rgba(37,40,45,0.3)]"
                  >
                  <Link
                    to={`/portfolio/${item.id}`}
                    className="block"
                    data-testid={`portfolio-link-${item.id}`}
                    aria-label={`View ${item.title} case study`}
                  >
                    <div className="relative overflow-hidden bg-offwhite">
                      <img
                        src={item.image}
                        alt={`${item.title} — ${item.category} concept design by SSDE LTD`}
                        className="w-full aspect-[4/3] object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
                        loading="lazy"
                      />
                      <span className="absolute top-4 left-4 bg-white/95 border border-line px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-gold-deep">
                        Concept
                      </span>
                    </div>
                    <div className="p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-charcoal/50">
                            {item.category}
                          </p>
                          <h2 className="mt-2 font-serif text-xl text-charcoal">{item.title}</h2>
                        </div>
                        <ArrowUpRight
                          size={18}
                          className="mt-1 text-gold-deep opacity-0 -translate-x-1 translate-y-1 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0 group-hover:translate-y-0"
                        />
                      </div>
                      <p className="mt-3 text-sm text-charcoal-mute leading-relaxed">{item.description}</p>
                    </div>
                  </Link>
                  </motion.article>
                ))}
              </AnimatePresence>
            </motion.div>
          )}

          <div className="mt-14 border border-line bg-offwhite px-8 py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
            <p className="text-sm text-charcoal-mute leading-relaxed max-w-xl">
              These are illustrative concept projects, created to show the standard
              and style of our work. Your project could be next.
            </p>
            <Link to="/contact" className="btn-gold shrink-0" data-testid="portfolio-cta-button">
              Start Your Project
              <ArrowUpRight size={15} strokeWidth={1.8} />
            </Link>
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
};

export default Portfolio;
